import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents } from '@shopify/events';

const KLARNA_WATCH_INTERVAL = 250;
const KLARNA_WATCH_ATTEMPTS = 20;

// HI engine A/B test exp-296651c5: dispatched by the purchase option (assets/simucube-purchase-option.js).
// Its selection stays null outside the variant arm, so in control the bar behaves exactly as before.
const PURCHASE_OPTION_CHANGE = 'simucube:purchase-option-change';

/**
 * Formats a price in cents using the shop's money format
 * (e.g. "€{{ amount_with_comma_separator }}").
 *
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const placeholder = format.match(placeholderRegex)?.[1] ?? 'amount';

  /**
   * @param {number} number
   * @param {number} precision
   * @param {string} thousands
   * @param {string} decimal
   */
  const formatWithDelimiters = (number, precision, thousands, decimal) => {
    const [whole = '0', fraction] = (number / 100).toFixed(precision).split('.');
    const wholeWithThousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? wholeWithThousands + decimal + fraction : wholeWithThousands;
  };

  let value;
  switch (placeholder) {
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2, ',', '.');
  }

  return format.replace(placeholderRegex, value);
}

/**
 * @typedef {object} ExtraLine
 * @property {number} price
 * @property {number} compare
 * @property {number} quantity
 */

/**
 * Sticky bundle bar for the product page. Tracks the selected variant,
 * quantity and any checked `[data-sc-bundle-toggle]` addon /
 * frequently-bought-together checkboxes in the section, renders the live
 * total (with compare-at price and percent-off badge when discounted), and
 * adds all items to the cart in a single request through
 * product-form-component's public addItems().
 *
 * @typedef {object} BundleBarRefs
 * @property {HTMLElement} total - The total price element.
 * @property {HTMLElement} [comparePrice] - The struck-through compare-at total.
 * @property {HTMLElement} [discountBadge] - The percent-off badge.
 * @property {HTMLButtonElement} [addToCartButton] - The add to cart button (absent when sold out).
 * @property {HTMLElement} [addToCartLabel] - The add to cart button label span.
 * @property {HTMLElement} [klarnaPlacement] - The Klarna on-site messaging placement.
 *
 * @extends {Component<BundleBarRefs>}
 */
class SimucubeBundleBar extends Component {
  requiredRefs = ['total'];

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {number} */
  #quantity = 1;

  /** @type {Map<string, ExtraLine>} */
  #extras = new Map();

  /** @type {boolean} */
  #adding = false;

  /**
   * A bundle chosen in the purchase option block. It replaces the main line,
   * because the bundle product already contains the wheelbase.
   * @type {{ variantId: string, price: number, compare: number, saveLabel?: string } | null}
   */
  #override = null;

  /** @type {string | null} */
  #idleLabel = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | undefined} */
  #klarnaRefreshTimer;

  /** @type {MutationObserver | null} */
  #klarnaObserver = null;

  /** @type {number | undefined} */
  #klarnaWatchTimer;

  /** @type {number} */
  #klarnaWatchAttempts = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#resizeObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--sc-bundle-bar-height', `${this.offsetHeight}px`);
    });
    this.#resizeObserver.observe(this);

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');

    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    section?.addEventListener('change', this.#handleToggleChange, { signal });
    section?.addEventListener('click', this.#handleQtyClick, { signal, capture: true });
    section?.addEventListener('input', this.#handleQtyInput, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartSettled, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartSettled, { signal });
    section?.addEventListener(PURCHASE_OPTION_CHANGE, this.#handlePurchaseOptionChange, { signal });

    this.#quantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
    this.#collectCheckedExtras();
    this.#readPurchaseOption();
    this.#renderTotal();
    this.#watchKlarnaMessage();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    clearTimeout(this.#klarnaRefreshTimer);
    this.#stopWatchingKlarna();
    document.documentElement.style.removeProperty('--sc-bundle-bar-height');
  }

  /**
   * Adds the main variant plus all checked extras to the cart in one request.
   */
  handleAddToCart = () => {
    if (this.dataset.variantAvailable !== 'true') return;

    const mainVariantId = this.dataset.mainVariantId;
    if (!mainVariantId) return;

    const productForm = /** @type {{ addItems?: Function } | null} */ (
      this.closest('.shopify-section')?.querySelector(
        `product-form-component[data-product-id="${this.dataset.productId}"]`,
      )
    );
    if (typeof productForm?.addItems !== 'function') return;

    const items = [{ variantId: mainVariantId, quantity: this.#quantity }];
    if (this.#override) items[0].variantId = this.#override.variantId;
    for (const [variantId, extra] of this.#extras) {
      items.push({ variantId, quantity: extra.quantity });
    }

    this.#adding = true;

    const label = this.refs.addToCartLabel;
    if (label) {
      this.#idleLabel = label.textContent;
      label.textContent = this.dataset.addingLabel || 'Adding…';
    }

    this.refs.addToCartButton?.setAttribute('aria-busy', 'true');
    productForm.addItems(items);
  };

  /**
   * Morphs the bar from the re-rendered section HTML when the selected
   * variant changes (same pattern as sticky-add-to-cart), so discount,
   * backorder and sold-out states stay server-rendered.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && detail.productId !== this.dataset.productId) return;

        const productId = this.dataset.productId;
        const next = productId
          ? detail.html.querySelector(`simucube-bundle-bar[data-product-id="${productId}"]`)
          : detail.html.querySelector('simucube-bundle-bar');
        if (!next) return;

        for (const attr of next.attributes) {
          if (attr.name === 'class' || attr.name.startsWith('data-')) {
            this.setAttribute(attr.name, attr.value);
          }
        }

        morph(this, next, { childrenOnly: true });
        this.#extras.clear();
        this.#collectCheckedExtras();
        // The purchase option block re-renders for the new variant and announces itself again.
        this.#readPurchaseOption();
        this.#renderTotal();
        this.#stopWatchingKlarna();
        this.#refreshKlarna();
        document.dispatchEvent(new CustomEvent('bubble-button:init', { detail: { root: this } }));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[simucube-bundle-bar] Event promise rejected:', error);
      });
  };

  /**
   * @param {Event} event
   */
  #handlePurchaseOptionChange = (event) => {
    this.#override = /** @type {CustomEvent} */ (event).detail ?? null;
    this.#renderTotal();
  };

  /**
   * Picks up a choice made before this bar connected (the two modules load independently).
   */
  #readPurchaseOption() {
    const option = /** @type {{ selection?: { variantId: string, price: number, compare: number, saveLabel?: string } | null } | null} */ (
      this.closest('.shopify-section')?.querySelector('simucube-purchase-option')
    );
    this.#override = option?.selection ?? null;
  }

  /**
   * Tracks addon / FBT checkbox changes anywhere in the section.
   * @param {Event} event
   */
  #handleToggleChange = (event) => {
    const toggle = event.target;
    if (!(toggle instanceof HTMLInputElement) || !toggle.hasAttribute('data-sc-bundle-toggle')) return;

    const variantId = toggle.dataset.variantId;
    if (!variantId) return;

    if (toggle.checked) {
      this.#extras.set(variantId, this.#toggleLine(toggle));
    } else {
      this.#extras.delete(variantId);
    }

    this.#renderTotal();
  };

  /**
   * @param {Event} event
   */
  #handleQtyClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const minusBtn = target.closest('[data-sc-bundle-qty-minus]');
    const plusBtn = target.closest('[data-sc-bundle-qty-plus]');
    if (!minusBtn && !plusBtn) return;

    event.preventDefault();
    event.stopPropagation();

    const wrap = target.closest('[data-sc-bundle-qty-wrap]');
    const qtyInput = wrap?.querySelector('[data-sc-bundle-qty]');
    if (!(qtyInput instanceof HTMLInputElement)) return;

    const variantId = qtyInput.dataset.variantId;
    const currentQty = parseInt(qtyInput.value || '1', 10) || 1;
    const nextQty = plusBtn ? Math.min(99, currentQty + 1) : Math.max(1, currentQty - 1);

    qtyInput.value = String(nextQty);

    if (variantId && this.#extras.has(variantId)) {
      const extra = this.#extras.get(variantId);
      if (extra) {
        extra.quantity = nextQty;
        this.#extras.set(variantId, extra);
        this.#renderTotal();
      }
    }
  };

  /**
   * @param {Event} event
   */
  #handleQtyInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-sc-bundle-qty')) return;

    const variantId = input.dataset.variantId;
    if (!variantId || !this.#extras.has(variantId)) return;

    const nextQty = Math.min(99, Math.max(1, parseInt(input.value || '1', 10) || 1));
    input.value = String(nextQty);

    const extra = this.#extras.get(variantId);
    if (extra) {
      extra.quantity = nextQty;
      this.#extras.set(variantId, extra);
      this.#renderTotal();
    }
  };

  /**
   * @param {import('@theme/events').QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#quantity = event.detail.quantity;
    this.#renderTotal();
  };

  /**
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #handleCartSettled = (event) => {
    if (!this.#adding) return;

    const restore = () => {
      this.#adding = false;
      this.refs.addToCartButton?.removeAttribute('aria-busy');

      const label = this.refs.addToCartLabel;
      if (label && this.#idleLabel != null) label.textContent = this.#idleLabel;
    };

    if (event.type === StandardEvents.cartError) {
      restore();
      return;
    }

    if (event.promise instanceof Promise) {
      event.promise
        .then((result) => {
          const detail = /** @type {{ detail?: { didError?: boolean } }} */ (result)?.detail;
          if (detail?.didError) return;
          this.#openCartDrawer();
          this.#clearExtras();
        })
        .finally(restore);
    } else {
      restore();
    }
  };

  #openCartDrawer() {
    if (this.dataset.openCartDrawer !== 'true') return;

    const drawer = /** @type {{ open?: Function, isOpen?: boolean } | null} */ (document.getElementById('cart-drawer'));
    if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
  }

  #clearExtras() {
    const toggles = this.closest('.shopify-section')?.querySelectorAll('input[data-sc-bundle-toggle]:checked') ?? [];

    for (const toggle of toggles) {
      if (toggle instanceof HTMLInputElement) toggle.checked = false;
    }

    this.#extras.clear();
    this.#renderTotal();
  }

  /**
   * Seeds the extras map from checkboxes rendered checked (preselected addon).
   */
  #collectCheckedExtras() {
    const toggles = this.closest('.shopify-section')?.querySelectorAll('input[data-sc-bundle-toggle]:checked') ?? [];

    for (const toggle of toggles) {
      if (!(toggle instanceof HTMLInputElement) || !toggle.dataset.variantId) continue;
      this.#extras.set(toggle.dataset.variantId, this.#toggleLine(toggle));
    }
  }

  /**
   * @param {HTMLInputElement} toggle
   * @returns {ExtraLine}
   */
  #toggleLine(toggle) {
    const price = parseInt(toggle.dataset.price ?? '0', 10) || 0;
    const compare = parseInt(toggle.dataset.compare ?? '0', 10) || price;
    const qtyInput = toggle.closest('.sc-bundle-item')?.querySelector('[data-sc-bundle-qty]');
    const quantity =
      qtyInput instanceof HTMLInputElement ? Math.min(99, Math.max(1, parseInt(qtyInput.value || '1', 10) || 1)) : 1;

    return { price, compare, quantity };
  }

  #renderTotal() {
    const mainPrice = parseInt(this.dataset.mainPrice ?? '0', 10) || 0;
    const mainCompare = parseInt(this.dataset.mainCompare ?? '0', 10) || mainPrice;

    let total = mainPrice * this.#quantity;
    let compareTotal = mainCompare * this.#quantity;
    if (this.#override) {
      total = this.#override.price * this.#quantity;
      compareTotal = (this.#override.compare || this.#override.price) * this.#quantity;
    }

    for (const { price, compare, quantity } of this.#extras.values()) {
      total += price * quantity;
      compareTotal += compare * quantity;
    }

    const format = this.dataset.moneyFormat || '{{ amount }}';
    this.classList.toggle('sc-bundle-bar--bundle', this.#override != null);
    this.refs.total.textContent = formatMoney(total, format);
    this.#syncKlarna(total);

    const { comparePrice, discountBadge } = this.refs;
    if (!comparePrice || !discountBadge) return;

    const mainHasDiscount = this.dataset.mainHasDiscount === 'true';

    const override = this.#override;
    if (override) {
      if (override.compare > override.price && compareTotal > total) {
        comparePrice.hidden = false;
        discountBadge.hidden = false;
        comparePrice.textContent = formatMoney(compareTotal, format);
        discountBadge.textContent = override.saveLabel?.includes('[amount]')
          ? override.saveLabel.replace('[amount]', formatMoney(compareTotal - total, format))
          : `${Math.round((1 - total / compareTotal) * 100)}% off`;
      } else {
        comparePrice.hidden = true;
        discountBadge.hidden = true;
      }
      return;
    }

    if (mainHasDiscount && compareTotal > total) {
      comparePrice.hidden = false;
      discountBadge.hidden = false;
      comparePrice.textContent = formatMoney(compareTotal, format);
      discountBadge.textContent = `${Math.round((1 - total / compareTotal) * 100)}% off`;
    } else {
      comparePrice.hidden = true;
      discountBadge.hidden = true;
    }
  }

  /** @param {number} total */
  #syncKlarna(total) {
    const placement = this.refs.klarnaPlacement;
    if (!placement) return;

    const amount = String(total);
    if (placement.dataset.purchaseAmount === amount) return;

    placement.dataset.purchaseAmount = amount;
    this.#refreshKlarna();
  }

  #refreshKlarna() {
    if (!this.refs.klarnaPlacement) return;

    clearTimeout(this.#klarnaRefreshTimer);
    this.#klarnaRefreshTimer = setTimeout(() => {
      window.Klarna?.OnsiteMessaging?.refresh();
      this.#watchKlarnaMessage();
    }, 300);
  }

  #watchKlarnaMessage() {
    if (this.#klarnaObserver || this.#klarnaWatchTimer) return;

    this.#klarnaWatchAttempts = 0;
    this.#klarnaWatchTimer = setInterval(() => {
      const root = this.refs.klarnaPlacement?.shadowRoot;

      if (root) {
        clearInterval(this.#klarnaWatchTimer);
        this.#klarnaWatchTimer = undefined;
        this.#klarnaObserver = new MutationObserver(() => this.#normalizeKlarnaMessage());
        this.#klarnaObserver.observe(root, { childList: true, subtree: true, characterData: true });
        this.#normalizeKlarnaMessage();
        return;
      }

      if (++this.#klarnaWatchAttempts >= KLARNA_WATCH_ATTEMPTS) {
        clearInterval(this.#klarnaWatchTimer);
        this.#klarnaWatchTimer = undefined;
      }
    }, KLARNA_WATCH_INTERVAL);
  }

  #stopWatchingKlarna() {
    clearInterval(this.#klarnaWatchTimer);
    this.#klarnaWatchTimer = undefined;
    this.#klarnaObserver?.disconnect();
    this.#klarnaObserver = null;
  }

  #normalizeKlarnaMessage() {
    const message = this.refs.klarnaPlacement?.shadowRoot?.querySelector('[part="osm-message"]');
    if (!message) return;

    let pastAmount = false;

    for (const node of message.childNodes) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (/** @type {Element} */ (node).tagName === 'STRONG') pastAmount = true;
        continue;
      }

      if (pastAmount && node.nodeType === Node.TEXT_NODE && node.nodeValue !== '') {
        node.nodeValue = '';
      }
    }

    for (const strong of message.querySelectorAll('strong')) {
      if (strong.style.fontWeight !== 'inherit') strong.style.fontWeight = 'inherit';
    }
  }
}

if (!customElements.get('simucube-bundle-bar')) {
  customElements.define('simucube-bundle-bar', SimucubeBundleBar);
}
